import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, MapPin, MessageCircle, UserPlus } from 'lucide-react';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import ListingCard from '../listings/ListingCard';
import PlayerModal from '../listings/PlayerModal';
import { usePref } from '../listings/embed';
import { PLATFORM_LABEL } from '../listings/labels';
import { SITE, ShareLinkButton } from '../listings/share';
import { track } from '../listings/track';
import { feedPath } from '../listings/countries';
import { BrandLoader } from '../brand/Brand';
import apiService from '../../api';

// An agent's page: /<country>/immobilier/agent/<id>-<slug>. Their listings in the feed (their own
// posts and the ones they reposted), their links, and a page they can share as "all my listings".
function AgentPage() {
  const { country, ref } = useParams();
  const navigate = useNavigate();
  const id = parseInt(ref, 10);
  const [agent, setAgent] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | not_found | error
  const [listings, setListings] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [player, setPlayer] = useState(null);
  const [hoverPlay] = usePref('hoverplay', true);

  useEffect(() => {
    if (!id) { setStatus('not_found'); return; }
    setStatus('loading');
    apiService.get(`/api/v1/listings/agents/${id}/`)
      .then((data) => {
        setAgent(data);
        setStatus('ok');
        // Short link, old name or other country: show the canonical address.
        if (window.location.pathname !== data.path) navigate(data.path, { replace: true });
      })
      .catch((e) => setStatus(e?.response?.status === 404 ? 'not_found' : 'error'));
  }, [id, navigate]);

  useEffect(() => {
    if (!agent) return;
    setLoading(true);
    apiService.get('/api/v1/listings/', { params: { country: agent.country, agent: agent.id, page } })
      .then((data) => {
        setListings((prev) => (page === 1 ? data.results : [...prev, ...data.results]));
        setHasMore(Boolean(data.next));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [agent, page]);

  const closePlayer = useCallback(() => setPlayer(null), []);
  const platform = agent ? PLATFORM_LABEL[agent.platform] : '';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>{agent ? `${agent.name} : annonces immobilières - Recouvro` : 'Agent - Recouvro'}</title>
        {agent && <meta name="description" content={`Toutes les annonces de ${agent.name} (${platform}) sur Recouvro : logements, maisons et terrains.`} />}
      </Helmet>
      <Header />
      <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <Link to={feedPath(country)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4">
          <ArrowLeft size={16} /> Toutes les annonces
        </Link>

        {status === 'loading' && <div className="flex justify-center py-20"><BrandLoader /></div>}
        {status === 'not_found' && <p className="text-center text-gray-500 py-20">Agent introuvable.</p>}
        {status === 'error' && <p className="text-center text-red-600 py-20">Impossible de charger cette page. Réessayez dans un instant.</p>}

        {status === 'ok' && agent && (
          <>
            <section className="bg-white rounded-xl shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="min-w-0 flex-1">
                <span className={`text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${agent.platform === 'tiktok' ? 'text-gray-900 border-gray-900' : 'text-blue-700 border-blue-700'}`}>
                  {platform}
                </span>
                <h1 className="mt-2 text-2xl font-bold text-gray-900 break-words">{agent.name}</h1>
                {agent.area && <p className="mt-1 text-sm text-gray-600 inline-flex items-center gap-1"><MapPin size={14} /> {agent.area}</p>}
                <p className="mt-1 text-sm text-gray-500">{agent.listings} annonce{agent.listings > 1 ? 's' : ''} · {agent.days} derniers jours</p>
              </div>
              <div className="flex flex-col sm:w-64 gap-2">
                {agent.whatsapp_url && (
                  <a href={agent.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2.5 text-sm font-semibold">
                    <MessageCircle size={16} /> Écrire sur WhatsApp
                  </a>
                )}
                <a href={agent.url} target="_blank" rel="noopener noreferrer" onClick={() => track('agent_profile')} className="flex items-center justify-center gap-2 border border-gray-300 bg-white text-gray-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50">
                  <ExternalLink size={16} /> Voir sur {platform}
                </a>
                <ShareLinkButton url={SITE + agent.path} title={`${agent.name} sur Recouvro`} text={`Toutes les annonces de ${agent.name} sur Recouvro`} label="Partager la page" />
              </div>
            </section>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
              {listings.map((l) => <ListingCard key={l.id} listing={l} hoverPlay={hoverPlay} onOpen={setPlayer} />)}
            </div>
            {!loading && listings.length === 0 && (
              <p className="text-center text-gray-500 py-12">Pas d'annonce de cet agent ces {agent.days} derniers jours.</p>
            )}
            {hasMore && (
              <div className="text-center mt-8">
                <button type="button" onClick={() => setPage((p) => p + 1)} disabled={loading} className="bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-100 disabled:opacity-50">
                  {loading ? 'Chargement…' : "Voir plus d'annonces"}
                </button>
              </div>
            )}

            <div className="mt-10 bg-blue-50 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-blue-900">
              <UserPlus size={20} className="shrink-0" />
              <p className="flex-1">Vous êtes agent immobilier ? Vos annonces TikTok et Facebook peuvent aussi apparaître sur Recouvro, gratuitement.</p>
              <Link to={`/${country}/immobilier/inscription-agent`} className="font-semibold underline">Ajouter mon compte</Link>
            </div>
          </>
        )}
      </main>
      <Footer />
      {player && <PlayerModal listing={player} onClose={closePlayer} />}
    </div>
  );
}

export default AgentPage;
